// Tiny fetch wrapper with bearer auth + standard error shape.
// Auto-refreshes the access token on 401 once per request.
import type { ApiErrorBody } from '@shared/contracts/errors';
import { getOnlineStatus } from '../platform/online-status';

const ACCESS_TOKEN_KEY = 'tccrm.accessToken';
const REFRESH_TOKEN_KEY = 'tccrm.refreshToken';

export function setAccessToken(token: string | null): void {
  if (token) localStorage.setItem(ACCESS_TOKEN_KEY, token);
  else localStorage.removeItem(ACCESS_TOKEN_KEY);
}
export function getAccessToken(): string | null {
  return localStorage.getItem(ACCESS_TOKEN_KEY);
}

/**
 * The refresh token is rotated on every /refresh round-trip, so we
 * persist it in localStorage too. localStorage is per-origin and the
 * Electron renderer is sole-tenant, so the trust boundary is the same as
 * for the access token; no XSS surface in the renderer (no user-injectable
 * HTML is rendered without sanitisation).
 */
export function setRefreshToken(token: string | null): void {
  if (token) localStorage.setItem(REFRESH_TOKEN_KEY, token);
  else localStorage.removeItem(REFRESH_TOKEN_KEY);
}
export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

export class ApiError extends Error {
  constructor(readonly status: number, readonly body: ApiErrorBody) {
    super(body.message);
  }
}

export interface ApiOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
  query?: { [k: string]: string | number | boolean | null | undefined };
}

// Backend base URL.
//   - In dev (Vite at http://127.0.0.1:5173) we use the relative `/api` so the
//     Vite proxy forwards to the express server.
//   - In packaged Electron, index.html loads via `app://` and there is no
//     proxy, so we must hit the express server's absolute URL.
//   - The main process injects the URL onto window via the preload script
//     (`window.tcCrm.apiBaseUrl`). If it is not present (e.g. dev), fall back
//     to `/api`.
function resolveApiBase(): string {
  const injected =
    typeof window !== 'undefined'
      ? (window as unknown as { tcCrm?: { apiBaseUrl?: string } }).tcCrm?.apiBaseUrl
      : undefined;
  if (typeof injected === 'string' && injected.length > 0) return injected.replace(/\/$/, '');
  if (typeof window !== 'undefined' && window.location.protocol === 'file:') {
    // Last-resort fallback for packaged builds where the preload didn't inject.
    return 'http://127.0.0.1:54330/api';
  }
  return '/api';
}

const API_BASE = resolveApiBase();

function buildUrl(path: string, query?: ApiOptions['query']): string {
  const isAbsolute = /^https?:\/\//i.test(API_BASE);
  if (isAbsolute) {
    const url = new URL(API_BASE + path);
    if (query) {
      for (const [k, v] of Object.entries(query)) {
        if (v !== undefined && v !== null) url.searchParams.set(k, String(v));
      }
    }
    return url.toString();
  }
  // Same-origin path (dev): keep it relative so the Vite proxy intercepts it.
  const params = new URLSearchParams();
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined && v !== null) params.set(k, String(v));
    }
  }
  const qs = params.toString();
  return API_BASE + path + (qs ? `?${qs}` : '');
}

/**
 * Single in-flight refresh promise so a burst of parallel 401s doesn't
 * trigger N concurrent /refresh calls (which would each rotate the token
 * and invalidate the others - only one would succeed).
 */
let refreshInflight: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  if (refreshInflight) return refreshInflight;
  // Offline → don't burn a refresh attempt against an unreachable server.
  // Return null so the caller falls back to its 401 path; the cached
  // `me` data + React Query persister keep the user usefully signed-in
  // for read-only access while disconnected.
  if (!getOnlineStatus().online) return null;
  const refresh = getRefreshToken();
  if (!refresh) return null;
  refreshInflight = (async () => {
    try {
      const url = buildUrl('/auth/refresh');
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: refresh }),
        credentials: 'include',
      });
      if (!res.ok) {
        // Refresh failed - drop both tokens so the next render bumps to login.
        setAccessToken(null);
        setRefreshToken(null);
        return null;
      }
      const json = (await res.json()) as { accessToken: string; refreshToken: string };
      setAccessToken(json.accessToken);
      setRefreshToken(json.refreshToken);
      return json.accessToken;
    } catch {
      setAccessToken(null);
      setRefreshToken(null);
      return null;
    } finally {
      refreshInflight = null;
    }
  })();
  return refreshInflight;
}

async function rawFetch(
  path: string,
  opts: ApiOptions,
  token: string | null,
): Promise<Response> {
  return fetch(buildUrl(path, opts.query), {
    method: opts.method ?? 'GET',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    signal: opts.signal,
    credentials: 'include',
  });
}

export async function api<T>(path: string, opts: ApiOptions = {}): Promise<T> {
  // Offline mutation guard. In offline view-only mode the renderer must
  // never attempt to write to the server - both because the network call
  // will fail and because allowing partial offline state would invite
  // sync conflicts. GETs pass through so React Query can fall back to its
  // persisted cache.
  const method = opts.method ?? 'GET';
  const isMutation = method !== 'GET';
  if (isMutation && !getOnlineStatus().online) {
    throw new ApiError(0, {
      code: 'AUTH_REQUIRED',
      message:
        'You are offline. Reconnect to make changes - the rest of the app remains available for viewing.',
    });
  }

  const token = getAccessToken();
  let res = await rawFetch(path, opts, token);

  // Try one refresh round-trip on a 401, but ONLY for non-auth routes.
  // Hitting /auth/refresh from the refresh path itself would loop forever,
  // and a failed login should still surface as a 401 to the caller.
  if (
    res.status === 401 &&
    !path.startsWith('/auth/login') &&
    !path.startsWith('/auth/refresh') &&
    !path.startsWith('/auth/signup') &&
    !path.startsWith('/auth/microsoft/callback')
  ) {
    const fresh = await refreshAccessToken();
    if (fresh) {
      res = await rawFetch(path, opts, fresh);
    }
  }

  if (res.status === 204) return undefined as unknown as T;

  const text = await res.text();
  const json = text ? (JSON.parse(text) as unknown) : null;
  if (!res.ok) {
    const body = (json ?? { code: 'INTERNAL_ERROR', message: res.statusText }) as ApiErrorBody;
    throw new ApiError(res.status, body);
  }
  return json as T;
}
